import { CalendarCustomWeek } from '@molecules/CalendarCustomWeek';
import { TimelineItem } from '@molecules/TimelineItem';
import {
  QueryKeys,
  useGetBulkEvents,
  useGetUserTicketsTemporal,
} from '@sunrise-ui/api-client';
import type { Event, Ticket } from '@sunrise-ui/api/events';
import { type TemporalRelation } from '@sunrise-ui/api/events';
import { TicketReceiptModal } from '@sunrise-ui/composites';
import {
  CacheAvailabilityProvider,
  Colors,
  Loader,
  TextReg,
  calendarTheme,
  isNative,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import { router, useLocalSearchParams } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { CalendarProvider, Timeline } from 'react-native-calendars/src';
import type { Event as CalendarEvent } from 'react-native-calendars/src/timeline/EventBlock';
import type { MarkedDates } from 'react-native-calendars/src/types';
import { Portal } from 'react-native-paper';

type TicketEventPair = {
  ticket: Ticket;
  event: Event;
};

type TicketTimelineEvent = CalendarEvent & { ticketEventPair: TicketEventPair };

type Props = {
  userId?: string;
  temporalRelation: TemporalRelation;
  allowPastSelection?: boolean;
};

export function TicketsCalendarDisplay({
  userId,
  temporalRelation,
  allowPastSelection = false,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const { date } = useLocalSearchParams<{ date?: string }>();
  const [selectedTicket, setSelectedTicket] = useState<TicketEventPair | null>(
    null,
  );

  const { data: tickets, isLoading: isTicketsLoading } =
    useGetUserTicketsTemporal({
      relation: temporalRelation,
      userId,
    });

  // Extract unique event IDs from active tickets
  const eventIds = useMemo(() => {
    if (!tickets) return undefined;
    const activeEventIds = tickets
      .filter((ticket) => ticket.is_active)
      .map((ticket) => ticket.event_id);
    return [...new Set(activeEventIds)];
  }, [tickets]);

  const { data: events, isLoading: isLoadingEvents } =
    useGetBulkEvents(eventIds);

  // Create ticket-event pairs with O(1) lookups using Map
  const ticketEventPairs = useMemo((): TicketEventPair[] => {
    if (!events || !tickets) return [];

    // Build Map for O(1) lookups instead of O(n²) nested iteration
    // Use first matching ticket per event (matching original .find() behavior)
    const activeTicketsByEvent = new Map<string, Ticket>();
    for (const ticket of tickets) {
      if (ticket.is_active && ticket.user_id === userId) {
        if (!activeTicketsByEvent.has(ticket.event_id)) {
          activeTicketsByEvent.set(ticket.event_id, ticket);
        }
      }
    }

    const pairs: TicketEventPair[] = [];
    for (const event of events) {
      // Only include internally ticketed events
      if (event.ticketing_platform_url || !event.id) continue;

      const ticket = activeTicketsByEvent.get(event.id);
      if (ticket) {
        pairs.push({ ticket, event });
      }
    }

    return pairs.sort(
      (a, b) =>
        new Date(a.event.start_date ?? '').getTime() -
        new Date(b.event.start_date ?? '').getTime(),
    );
  }, [events, tickets, userId]);

  const isLoading = isTicketsLoading || isLoadingEvents;

  const minDate = useMemo(() => {
    if (ticketEventPairs.length > 0) {
      const firstPair = ticketEventPairs[0];
      if (firstPair.event.start_date) {
        const firstDate = DateTime.fromISO(firstPair.event.start_date);
        if (firstDate.isValid) return firstDate;
      }
    }
    return DateTime.now();
  }, [ticketEventPairs]);

  const selectedDate = useMemo(() => {
    const parsedDate = DateTime.fromISO(date ?? '');
    if (parsedDate.isValid) return parsedDate;
    return minDate;
  }, [date, minDate]);

  // Create marked dates for the calendar
  const markedDates = useMemo((): MarkedDates => {
    const marked: MarkedDates = {};

    for (const pair of ticketEventPairs) {
      if (!pair.event.start_date) continue;
      const eventDate = DateTime.fromISO(pair.event.start_date);
      const dateKey = eventDate.toISODate();
      if (!dateKey) continue;

      if (!marked[dateKey]) {
        marked[dateKey] = {
          marked: true,
          dotColor: Colors.pink0,
          dots: [{ color: Colors.pink0 }],
        };
      } else {
        const existingDots = marked[dateKey].dots ?? [];
        if (existingDots.length < 4) {
          marked[dateKey] = {
            ...marked[dateKey],
            dots: [...existingDots, { color: Colors.pink0 }],
          };
        }
      }
    }

    return marked;
  }, [ticketEventPairs]);

  const { timelineEvents, timelineRange } = useMemo(() => {
    if (ticketEventPairs.length === 0) {
      return {
        timelineEvents: [] as TicketTimelineEvent[],
        timelineRange: { start: 0, end: 24 },
      };
    }

    let earliestHour = 24;
    let latestHour = 0;

    const timelineEvents: TicketTimelineEvent[] = ticketEventPairs
      .filter((pair) => {
        if (!pair.event.start_date || !pair.event.scheduled_end_date)
          return false;
        const startDate = DateTime.fromISO(pair.event.start_date);
        const endDate = DateTime.fromISO(pair.event.scheduled_end_date);
        return (
          selectedDate >= startDate.startOf('day') &&
          selectedDate <= endDate.endOf('day')
        );
      })
      .map((pair) => {
        const startDate = DateTime.fromISO(pair.event.start_date!);
        const endDate = DateTime.fromISO(pair.event.scheduled_end_date!);

        const isStartingToday = startDate.hasSame(selectedDate, 'day');
        const isEndingToday = endDate.hasSame(selectedDate, 'day');

        if (!isStartingToday) earliestHour = 0;
        else if (startDate.hour < earliestHour) earliestHour = startDate.hour;

        if (!isEndingToday) latestHour = 24;
        else if (endDate.hour > latestHour) latestHour = endDate.hour;

        const start = isStartingToday ? startDate : selectedDate.startOf('day');
        const end = isEndingToday ? endDate : selectedDate.endOf('day');

        return {
          id: pair.ticket.id ?? pair.event.id ?? '',
          start: start.toFormat('yyyy-MM-dd HH:mm:ss'),
          end: end.toFormat('yyyy-MM-dd HH:mm:ss'),
          title: pair.event.name ?? '',
          color: 'transparent',
          ticketEventPair: pair,
        };
      });

    const timelineRange = {
      start: Math.max(0, earliestHour - 1),
      end: Math.min(24, latestHour + 2),
    };

    return {
      timelineEvents,
      timelineRange,
    };
  }, [ticketEventPairs, selectedDate]);

  const onDateChanged = useCallback((newDate: string) => {
    router.setParams({ date: newDate });
  }, []);

  const handleTicketPress = useCallback((pair: TicketEventPair) => {
    setSelectedTicket(pair);
  }, []);

  const { eventsKey } = QueryKeys.events;

  if (isLoading) return <Loader />;

  return (
    <CacheAvailabilityProvider queryKeys={new Set([[eventsKey]])}>
      <Portal.Host>
        <View style={styles.container}>
          <CalendarProvider
            date={selectedDate.toISODate()}
            onDateChanged={onDateChanged}
            showTodayButton
            theme={calendarTheme}
          >
            <CalendarCustomWeek
              selectedDate={selectedDate}
              onDateChanged={onDateChanged}
              markedDates={markedDates}
              allowPastSelection={allowPastSelection}
            />
            {timelineEvents.length === 0 ? (
              <View style={styles.emptyStateContainer}>
                <TextReg style={styles.emptyStateSubtitle}>
                  You have no tickets for{' '}
                  {selectedDate.toFormat('EEEE, MMMM d')}
                </TextReg>
              </View>
            ) : (
              <Timeline
                date={selectedDate.toISODate()}
                events={timelineEvents}
                rightEdgeSpacing={10}
                timelineLeftInset={isNative ? 64 : 70}
                renderEvent={(event) => {
                  const { ticketEventPair } =
                    event as unknown as TicketTimelineEvent;
                  return (
                    <Pressable
                      onPress={() => handleTicketPress(ticketEventPair)}
                      style={styles.timelineItemPressable}
                    >
                      <TimelineItem
                        title={ticketEventPair.event.name}
                        eventId={ticketEventPair.event.id}
                      />
                    </Pressable>
                  );
                }}
                theme={calendarTheme}
                start={Math.min(timelineRange.start, 9)}
                end={Math.max(timelineRange.end, 23)}
                scrollToFirst={true}
                format24h={false}
              />
            )}
          </CalendarProvider>
          <TicketReceiptModal
            visible={!!selectedTicket}
            onDismiss={() => setSelectedTicket(null)}
            ticket={selectedTicket?.ticket}
            event={selectedTicket?.event}
          />
        </View>
      </Portal.Host>
    </CacheAvailabilityProvider>
  );
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    emptyStateContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 32,
      paddingVertical: 64,
    },
    emptyStateSubtitle: {
      fontSize: 16,
      textAlign: 'center',
    },
    timelineItemPressable: {
      flex: 1,
    },
  });
